import { LegacyCard } from '@shopify/polaris';
import { useContext, useState } from 'react';
import ToastContext from '../../../context/ToastContext';
import { SET_ERROR, SET_OK } from '../../../context/ToastReducer';
import { getTransferStockById } from '../../../services/transfersService';
import ProductCard from '../Products/ProductCard';
import ProductFindCard from '../Products/ProductFindCard';
import TransferReceiveStockTable from '../Tables/TransferReceiveStockTable';
import { useTransferMutations } from '../../../customHooks/useTransferQueries';

/**
 * Tarjeta para anotar la recepción de mercancía cuando la transferencia está
 * en IN_TRANSIT.
 *
 * Reusa el mismo patrón visual que AddStockCard (LegacyCard +
 * ProductFindCard + ProductCard + tabla) pero adaptado al flujo de
 * recepción:
 *
 *   - El buscador filtra dentro del stock de la transferencia
 *     (`GET /transfers/:id/stock?s=`), no en el catálogo global. Solo se
 *     puede anotar recepción de productos que efectivamente están en la
 *     transferencia.
 *   - La tabla `TransferReceiveStockTable` muestra Enviado / Recibido /
 *     Anotar y emite deltas (cuánto cambia el "recibido" tras la edición).
 *   - El submit llama a `mutations.receive` con el array de deltas. La
 *     mutación invalida detalle y stock automáticamente, así que la pantalla
 *     se refresca sola.
 */
const ReceiveStockCard = ({ transfer }) => {
  const [productFound, setProductFound] = useState(null);
  const [productListFound, setProductListFound] = useState([]);
  const [deltasToSubmit, setDeltasToSubmit] = useState([]);
  const [, dispatchToast] = useContext(ToastContext);

  const { receive } = useTransferMutations(transfer.id);

  const handleSearch = (family) =>
    getTransferStockById(transfer.id, { s: family }).then((r) => {
      const items = r.data?.data ?? [];
      if (items.length === 1) {
        setProductFound(items[0]);
        setProductListFound([]);
      } else {
        setProductFound(null);
        setProductListFound(items.map(({ family: f }) => f));
      }
    });

  const handleClearSearch = () => {
    setProductFound(null);
    setProductListFound([]);
    setDeltasToSubmit([]);
  };

  const handleDeltasChange = (deltas) => {
    // El TransferReceiveStockTable ya nos da deltas con `qty` (puede ser
    // negativo si el operador corrige hacia abajo). Para el endpoint receive
    // necesitamos `consolidatedQty`. Hacemos la traducción aquí para no
    // tener que tocar la tabla si el contrato cambiase.
    setDeltasToSubmit(deltas.map((d) => ({ barcode: d.barcode, consolidatedQty: d.qty })));
  };

  // Re-fetch del producto seleccionado tras un submit exitoso para que la
  // tabla muestre los receivedQty nuevos sin que el operador tenga que volver
  // a buscar. El cambio de la suma de receivedQty también dispara el
  // remount de la tabla (ver `key` abajo) y resetea los inputs AÑADIR a 0.
  const refreshSelectedProduct = () => {
    if (!productFound) return;
    getTransferStockById(transfer.id, { s: productFound.family }).then((r) => {
      const items = r.data?.data ?? [];
      const updated = items.find((p) => p.id === productFound.id);
      if (updated) setProductFound(updated);
    });
  };

  const handleSubmit = () => {
    if (deltasToSubmit.length === 0) return;

    receive.mutate(deltasToSubmit, {
      onSuccess: (r) => {
        dispatchToast({ type: SET_OK, payload: r?.data || { message: 'Recepción anotada' } });
        refreshSelectedProduct();
        setDeltasToSubmit([]);
      },
      onError: (e) => {
        dispatchToast({
          type: SET_ERROR,
          payload: e.response?.data || { message: 'No se pudo anotar la recepción' },
        });
      },
    });
  };

  return (
    <LegacyCard
      title="Anotar recepción"
      primaryFooterAction={
        productFound && {
          content: 'Anotar recepción',
          disabled: !deltasToSubmit.length || receive.isLoading,
          loading: receive.isLoading,
          onAction: handleSubmit,
        }
      }
      secondaryFooterActions={
        productFound && [{ content: 'Limpiar', onAction: handleClearSearch, destructive: true, plain: true }]
      }
    >
      <LegacyCard.Section>
        <ProductFindCard
          onSearch={handleSearch}
          onClearSearch={handleClearSearch}
          product={productFound}
          optionList={productListFound}
        >
          {productFound && (
            <ProductCard product={productFound} wide={true}>
              {/* La key incluye la suma de receivedQty: cuando cambia tras un
                  submit exitoso, la tabla se remonta y los inputs AÑADIR
                  vuelven a 0. */}
              <TransferReceiveStockTable
                key={`${productFound.id}-${(productFound.stock || []).reduce((acc, s) => acc + (s.receivedQty || 0), 0)}`}
                product={productFound}
                onAddQty={handleDeltasChange}
              />
            </ProductCard>
          )}
        </ProductFindCard>
      </LegacyCard.Section>
    </LegacyCard>
  );
};

export default ReceiveStockCard;
